/**
 * useAccessClaim — claim any email-allowlist (pre-granted) free access.
 *
 * Posts to /api/access/claim with the signed-in user's access token. The server
 * derives the email from the verified session, finds any active, unredeemed
 * email_access_grants for it, and applies them. Idempotent: returns granted:0
 * when there's nothing pending, so it's safe to call on EVERY sign-in.
 *
 * If anything was granted, refreshes the shared entitlements so the UI unlocks
 * without a reload.
 */

import { ref } from 'vue'
import { useSharedUserEntitlements } from '@/composables/useUserEntitlements'
import { bootFetch } from '../playback/signedInBoot'

// Single-flight per session token. Both the onAuthStateChange SIGNED_IN
// listener and App.vue's restored-session boot path claim for the same
// session, so without this every boot POSTed /api/access/claim twice.
// One claim per token is enough — the endpoint is idempotent, and a genuinely
// new sign-in arrives with a new token.
// Job #402: grants held back because this session never proved its mailbox.
// Set from /api/access/claim's `held`; GrantProofPrompt.vue reads it, mails a
// code, verifies it at /api/email/verify, then calls reclaim().
export const heldGrants = ref(0)

/** The schools / org surfaces read the staff role once; a fresh load re-reads it. */
export function reloadIfOnSchoolsSurface(): void {
  if (typeof window === 'undefined') return
  const path = window.location.pathname
  if (path === '/schools' || path.startsWith('/schools/') || path === '/org' || path.startsWith('/org/')) {
    window.location.reload()
  }
}

let claimedToken: string | null = null
let claimInFlight: Promise<number> | null = null
let claimGeneration = 0

export function useAccessClaim() {
  /**
   * Claim allowlist grants for the current session.
   * @param accessToken Supabase session access token.
   * @returns number of grants applied (0 if none / on any failure).
   */
  async function claimAccess(accessToken: string): Promise<number> {
    if (!accessToken) return 0
    if (accessToken === claimedToken) return claimInFlight ?? 0
    claimedToken = accessToken
    claimInFlight = doClaim(accessToken)
    return claimInFlight
  }

  async function doClaim(accessToken: string): Promise<number> {
    const mine = ++claimGeneration
    try {
      const res = await bootFetch('/api/access/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })
      if (!res.ok) return 0
      const data = await res.json().catch(() => ({}))
      const granted: number = data?.granted ?? 0
      // A reclaim or new session may have superseded this response while it loaded.
      if (mine !== claimGeneration) return granted
      heldGrants.value = data?.mailboxUnproven ? (data?.held ?? 0) : 0
      // Added to a school's staff by email before they had an account (job #166):
      // this sign-in just made them staff, but the role was read before the claim
      // landed. On the schools surface, reload once to pick it up, exactly as the
      // join-code path does after redeeming.
      if ((data?.staffJoined ?? 0) > 0) reloadIfOnSchoolsSurface()
      if (granted > 0) {
        try {
          await useSharedUserEntitlements().refresh()
        } catch {
          // entitlements refresh is best-effort
        }
      }
      return granted
    } catch {
      // Allowlist claim must never break sign-in — swallow.
      return 0
    }
  }

  /** Claim again with the same token — the server re-reads mailbox proof on every call. */
  async function reclaim(accessToken: string): Promise<number> {
    if (!accessToken) return 0
    claimedToken = accessToken
    claimInFlight = doClaim(accessToken)
    return claimInFlight
  }

  return { claimAccess, reclaim }
}
